import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormGuardActions } from '@/entities/formGuard';
import type { PhrasePick, TranscriptEditorForm } from './types';

/**
 * Состояние режима редактирования расшифровки: открытая форма, признак её несохранённых правок
 * и ожидание выбора реплики нажатием в ленте.
 *
 * Держится у секции расшифровки, а не у ленты режима: выход из режима в шапке секции
 * спрашивает подтверждение по тому же признаку, которым правка защищена от ухода со страницы.
 */
export const useTranscriptEditorState = () => {
  const [openedForm, setOpenedForm] = useState<TranscriptEditorForm | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [phrasePick, setPhrasePick] = useState<PhrasePick | null>(null);
  const { setIsDirty: setFormGuardDirty, resetFormGuard } = useFormGuardActions();

  // Общий признак несохранённых правок снимается, только если его поднял режим: чужую правку
  // на той же странице режим не трогает
  const hasRaisedFormGuardRef = useRef(false);
  useEffect(() => {
    if (isDirty) {
      setFormGuardDirty(true);
      hasRaisedFormGuardRef.current = true;
      return;
    }
    if (hasRaisedFormGuardRef.current) {
      setFormGuardDirty(false);
      hasRaisedFormGuardRef.current = false;
    }
  }, [isDirty, setFormGuardDirty]);
  useEffect(
    () => () => {
      if (hasRaisedFormGuardRef.current) resetFormGuard();
    },
    [resetFormGuard],
  );

  const openForm = useCallback((form: TranscriptEditorForm) => {
    setOpenedForm(form);
    setIsDirty(false);
    setPhrasePick(null);
  }, []);

  const closeForm = useCallback(() => {
    setOpenedForm(null);
    setIsDirty(false);
    setPhrasePick(null);
  }, []);

  return useMemo(
    () => ({
      openedForm,
      isDirty,
      phrasePick,
      openForm,
      closeForm,
      /** Форма сообщает, есть ли в ней несохранённые правки */
      reportDirty: setIsDirty,
      startPhrasePick: setPhrasePick,
      stopPhrasePick: () => setPhrasePick(null),
    }),
    [openedForm, isDirty, phrasePick, openForm, closeForm],
  );
};

/** Состояние режима редактирования расшифровки — см. {@link useTranscriptEditorState} */
export type TranscriptEditorState = ReturnType<typeof useTranscriptEditorState>;
